import { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useLocalSearchParams } from "expo-router";
import { createRun, updateRun, getDog, searchEvents, EventSearchHit } from "../../lib/api";
import { calculateSpeed, calculatePoints, calculateHandicap } from "../../lib/calculations";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";

function todayString(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function LogRunScreen() {
  const router = useRouter();
  const { dogId, editId, editDate, editTime, editRunNumber, editNotes, editLocation, editEventName, editLureOperator } = useLocalSearchParams<{
    dogId: string;
    editId?: string;
    editDate?: string;
    editTime?: string;
    editRunNumber?: string;
    editNotes?: string;
    editLocation?: string;
    editEventName?: string;
    editLureOperator?: string;
  }>();
  const isEditing = !!editId;
  const [date, setDate] = useState(editDate || todayString());
  const [runNumber, setRunNumber] = useState(editRunNumber ? parseInt(editRunNumber) : 0);
  const [timeInput, setTimeInput] = useState(editTime || "");
  const [notes, setNotes] = useState(editNotes || "");
  const [location, setLocation] = useState(editLocation || "");
  const [eventName, setEventName] = useState(editEventName || "");
  const [lureOperator, setLureOperator] = useState(editLureOperator || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dogName, setDogName] = useState("");
  const [dogHandicap, setDogHandicap] = useState<number | null>(null);

  // Event-name autocomplete state. Debounced search against /api/events/search;
  // selecting a hit fills the event name + location (and lure operator when
  // we have one from the premium-list scrape).
  const [eventHits, setEventHits] = useState<EventSearchHit[]>([]);
  const [eventHitsOpen, setEventHitsOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!eventName || eventName.trim().length < 2) { setEventHits([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const hits = await searchEvents(eventName);
        setEventHits(hits);
      } catch { setEventHits([]); }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [eventName]);

  useEffect(() => {
    if (dogId) {
      getDog(dogId)
        .then((dog) => {
          setDogName(dog.name);
          if (dog.heightInches) {
            const h = parseFloat(dog.heightInches);
            if (!isNaN(h)) setDogHandicap(calculateHandicap(h));
          }
          if (dog.handicap) {
            const hc = parseFloat(dog.handicap);
            if (!isNaN(hc)) setDogHandicap(hc);
          }
        })
        .catch(() => {});
    }
  }, [dogId]);

  const timeNum = parseFloat(timeInput);
  const validTime = !isNaN(timeNum) && timeNum > 0;
  const speed = validTime ? calculateSpeed(timeNum) : null;
  const points = validTime && dogHandicap !== null ? calculatePoints(speed!, dogHandicap) : null;

  async function handleSubmit() {
    if (!date.trim()) {
      setError("Date is required");
      return;
    }
    if (!validTime) {
      setError("Please enter a valid time in seconds");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setError("Date must be in YYYY-MM-DD format");
      return;
    }

    setLoading(true);
    setError("");
    try {
      if (isEditing) {
        // On edit, always send all editable fields (null when blank) so clearing works.
        await updateRun(editId!, {
          date: date.trim(),
          timeSeconds: timeNum,
          runNumber,
          notes: notes.trim() || null,
          location: location.trim() || null,
          eventName: eventName.trim() || null,
          lureOperator: lureOperator.trim() || null,
        } as any);
      } else {
        await createRun(dogId!, {
          date: date.trim(),
          runNumber,
          timeSeconds: timeNum,
          notes: notes.trim() || undefined,
          location: location.trim() || undefined,
          eventName: eventName.trim() || undefined,
          lureOperator: lureOperator.trim() || undefined,
        } as any);
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        window.location.href = `/dogs/${dogId}`;
      } else {
        router.replace(`/(tabs)/dogs/${dogId}` as any);
      }
    } catch (err: any) {
      setError(err.message || "Failed to save run");
    } finally {
      setLoading(false);
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#f9fafb" }}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 16,
            paddingVertical: 14,
            backgroundColor: "#ffffff",
            borderBottomWidth: 1,
            borderBottomColor: "#e5e7eb",
          }}
        >
          <TouchableOpacity onPress={() => { if (Platform.OS === "web" && typeof window !== "undefined") { window.location.href = `/dogs/${dogId}`; } else { router.back(); } }} style={{ marginRight: 12 }}>
            <Text style={{ color: "#2563eb", fontSize: 16 }}>← Back</Text>
          </TouchableOpacity>
          <View>
            <Text style={{ fontSize: 18, fontWeight: "700", color: "#111827" }}>{isEditing ? "Edit Run" : "Log a Run"}</Text>
            {dogName ? (
              <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 1 }}>{dogName}</Text>
            ) : null}
          </View>
        </View>

        <ScrollView contentContainerStyle={{ padding: 20 }}>
          {error ? (
            <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
            </View>
          ) : null}

          <Input
            label="Date (YYYY-MM-DD)"
            value={date}
            onChangeText={setDate}
            placeholder="2025-07-04"
            keyboardType="default"
            autoCapitalize="none"
          />

          {/* Run number picker */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 14, fontWeight: "500", color: "#374151", marginBottom: 8 }}>Run Number</Text>
            <View style={{ flexDirection: "row", gap: 10 }}>
              {([
                { num: 1, label: "Run 1", desc: "1st run" },
                { num: 2, label: "Run 2", desc: "2nd run" },
                { num: 0, label: "N/A", desc: "Unknown" },
              ] as const).map((opt) => (
                <TouchableOpacity
                  key={opt.num}
                  onPress={() => setRunNumber(opt.num)}
                  style={{
                    flex: 1,
                    paddingVertical: 14,
                    borderRadius: 10,
                    borderWidth: 2,
                    borderColor: runNumber === opt.num ? "#2563eb" : "#d1d5db",
                    backgroundColor: runNumber === opt.num ? "#eff6ff" : "#fff",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 60,
                  }}
                >
                  <Text style={{
                    fontSize: 16,
                    fontWeight: "700",
                    color: runNumber === opt.num ? "#2563eb" : "#374151",
                  }}>
                    {opt.label}
                  </Text>
                  <Text style={{ fontSize: 13, color: runNumber === opt.num ? "#1e40af" : "#9ca3af", marginTop: 2 }}>
                    {opt.desc}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <Input
            label="Time (seconds)"
            value={timeInput}
            onChangeText={setTimeInput}
            placeholder="e.g. 6.23 or 6.235"
            keyboardType="decimal-pad"
          />

          {/* Live preview */}
          {validTime && (
            <View
              style={{
                backgroundColor: "#eff6ff",
                borderRadius: 14,
                padding: 18,
                marginBottom: 20,
              }}
            >
              <Text style={{ fontSize: 15, fontWeight: "700", color: "#1e40af", marginBottom: 12 }}>
                Run Preview
              </Text>
              <View style={{ flexDirection: "row", justifyContent: "space-around" }}>
                <View style={{ alignItems: "center" }}>
                  <Text style={{ fontSize: 22, fontWeight: "800", color: "#2563eb" }}>
                    {speed!.toFixed(2)}
                  </Text>
                  <Text style={{ fontSize: 14, color: "#3b82f6", marginTop: 2 }}>mph</Text>
                </View>
                {points !== null && (
                  <>
                    <View style={{ width: 1, backgroundColor: "#bfdbfe" }} />
                    <View style={{ alignItems: "center" }}>
                      <Text style={{ fontSize: 22, fontWeight: "800", color: "#2563eb" }}>
                        {points.toFixed(2)}
                      </Text>
                      <Text style={{ fontSize: 14, color: "#3b82f6", marginTop: 2 }}>points</Text>
                    </View>
                    <View style={{ width: 1, backgroundColor: "#bfdbfe" }} />
                    <View style={{ alignItems: "center" }}>
                      <Text style={{ fontSize: 22, fontWeight: "800", color: "#2563eb" }}>
                        {dogHandicap!.toFixed(1)}×
                      </Text>
                      <Text style={{ fontSize: 14, color: "#3b82f6", marginTop: 2 }}>handicap</Text>
                    </View>
                  </>
                )}
              </View>
            </View>
          )}

          {/* Event Name with autocomplete — search hits AKC events table.
              Tapping a hit fills the event name + location (+ lure op if known). */}
          <View style={{ marginBottom: 16 }}>
            <Input
              label="Event Name (optional)"
              value={eventName}
              onChangeText={(v) => { setEventName(v); setEventHitsOpen(true); }}
              placeholder="Start typing club or venue city…"
              autoCapitalize="words"
            />
            {eventHitsOpen && eventHits.length > 0 && (
              <View style={{ backgroundColor: "#fff", borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 10, marginTop: -12, marginBottom: 14, overflow: "hidden" }}>
                {eventHits.map((hit, i) => (
                  <TouchableOpacity
                    key={`${hit.club}-${hit.city}-${i}`}
                    onPress={() => {
                      setEventName(hit.club);
                      if (hit.city && hit.state) setLocation(`${hit.city}, ${hit.state}`);
                      if (hit.lure_operator && !lureOperator.trim()) setLureOperator(hit.lure_operator);
                      setEventHitsOpen(false);
                    }}
                    style={{ paddingHorizontal: 14, paddingVertical: 10, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: "#f3f4f6" }}
                  >
                    <Text style={{ fontSize: 14, color: "#111827", fontWeight: "600" }} numberOfLines={1}>{hit.club}</Text>
                    {(hit.city || hit.state) ? (
                      <Text style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }} numberOfLines={1}>
                        {[hit.city, hit.state].filter(Boolean).join(", ")}{hit.lure_operator ? ` · lure: ${hit.lure_operator}` : ""}
                      </Text>
                    ) : null}
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          <Input
            label="Location (optional)"
            value={location}
            onChangeText={setLocation}
            placeholder="City, State (e.g. Freehold, NJ)"
            autoCapitalize="words"
          />

          <Input
            label="Lure Operator (optional)"
            value={lureOperator}
            onChangeText={setLureOperator}
            placeholder="Name of the person running the lure"
            autoCapitalize="words"
          />

          <Input
            label="Notes (optional)"
            value={notes}
            onChangeText={setNotes}
            placeholder="Weather, surface, event name, how your dog felt..."
            autoCapitalize="sentences"
            multiline
            numberOfLines={5}
          />

          <View style={{ marginTop: 8 }}>
            <Button title={isEditing ? "Update Run" : "Save Run"} onPress={handleSubmit} loading={loading} />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
